import { useState } from "react";
import { Link } from "react-router-dom";
import BoltIcon from "@heroicons/react/24/outline/BoltIcon";
import FireIcon from "@heroicons/react/24/outline/FireIcon";
import TrophyIcon from "@heroicons/react/24/outline/TrophyIcon";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import ClockIcon from "@heroicons/react/24/outline/ClockIcon";
import ChartBarIcon from "@heroicons/react/24/outline/ChartBarIcon";
import ArrowTrendingDownIcon from "@heroicons/react/24/outline/ArrowTrendingDownIcon";
import CalendarDaysIcon from "@heroicons/react/24/outline/CalendarDaysIcon";
import SunIcon from "@heroicons/react/24/outline/SunIcon";
import SparklesIcon from "@heroicons/react/24/outline/SparklesIcon";
import HeartIcon from "@heroicons/react/24/outline/HeartIcon";
import MoonIcon from "@heroicons/react/24/outline/MoonIcon";
import ArrowRightIcon from "@heroicons/react/24/outline/ArrowRightIcon";
import { useStatistics } from "../hooks/useStatistics";
import { useDashboardPlans } from "../hooks/useDashboardPlans";
import { useRecentActivities } from "../hooks/useRecentActivities";
import type { TimeRange, WeeklyZ2Pace, TrainingPlanWithMetrics, PlannedSession, SessionType, ActivityListItem } from "@runlens/shared";
import { SessionDetailModal } from "../components/planner/SessionDetailModal";
import { GoalMetrics } from "../components/planner/GoalMetrics";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from "recharts";

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: "1w", label: "1 Week" },
  { value: "1m", label: "1 Month" },
  { value: "6m", label: "6 Months" },
  { value: "1y", label: "1 Year" },
];

function formatDistance(meters: number): string {
  const km = meters / 1000;
  if (km >= 100) {
    return `${Math.round(km)} km`;
  }
  return `${km.toFixed(1)} km`;
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);

  if (hrs === 0) {
    return `${mins}m`;
  }
  if (mins === 0) {
    return `${hrs}h`;
  }
  return `${hrs}h ${mins}m`;
}

function formatCalories(calories: number): string {
  if (calories >= 1000) {
    return `${(calories / 1000).toFixed(1)}k`;
  }
  return calories.toString();
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
}

function StatCard({ icon, label, value, subtitle }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">{icon}</div>
        <span className="text-sm font-medium text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {subtitle && (
        <div className="text-sm text-gray-500 mt-1">{subtitle}</div>
      )}
    </div>
  );
}

interface WorkoutBreakdownCardProps {
  treadmill: number;
  outdoor: number;
}

function WorkoutBreakdownCard({ treadmill, outdoor }: WorkoutBreakdownCardProps) {
  const total = treadmill + outdoor;
  const treadmillPct = total > 0 ? Math.round((treadmill / total) * 100) : 0;
  const outdoorPct = total > 0 ? Math.round((outdoor / total) * 100) : 0;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
          <ChartBarIcon className="w-5 h-5" />
        </div>
        <span className="text-sm font-medium text-gray-500">Workout Types</span>
      </div>

      {total === 0 ? (
        <div className="text-gray-400 text-sm">No workouts yet</div>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Outdoor</span>
            <span className="text-sm font-medium text-gray-900">
              {outdoor} ({outdoorPct}%)
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full"
              style={{ width: `${outdoorPct}%` }}
            />
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Treadmill</span>
            <span className="text-sm font-medium text-gray-900">
              {treadmill} ({treadmillPct}%)
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-indigo-500 h-2 rounded-full"
              style={{ width: `${treadmillPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Format pace (sec/km) to min:sec string
function formatPace(secPerKm: number): string {
  const mins = Math.floor(secPerKm / 60);
  const secs = Math.round(secPerKm % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Format week date for display (e.g., "Jan 13")
function formatWeekLabel(weekDate: string): string {
  const date = new Date(weekDate);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

interface Z2PaceChartProps {
  data: WeeklyZ2Pace[];
}

function Z2PaceChart({ data }: Z2PaceChartProps) {
  if (data.length === 0) {
    return null;
  }

  // Sort by date (oldest first for chart)
  const sortedData = [...data].sort((a, b) => a.week.localeCompare(b.week));

  // Transform for recharts
  const chartData = sortedData.map((d) => ({
    week: d.week,
    weekLabel: formatWeekLabel(d.week),
    pace: d.avgZ2Pace / 60, // Convert sec/km to min/km for Y-axis
    paceRaw: d.avgZ2Pace,
  }));

  // Calculate pace improvement (first week vs last week)
  const firstPace = chartData[0]?.paceRaw;
  const lastPace = chartData[chartData.length - 1]?.paceRaw;
  const improvement = firstPace && lastPace ? firstPace - lastPace : 0;
  const improvementPct = firstPace ? Math.round((improvement / firstPace) * 100) : 0;

  // Get min/max for Y-axis domain (with padding)
  const paces = chartData.map((d) => d.pace);
  const minPace = Math.max(Math.min(...paces) - 0.5, 0);
  const maxPace = Math.max(...paces) + 0.5;

  return (
    <div className="bg-white rounded-lg shadow p-6 col-span-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
            <ArrowTrendingDownIcon className="w-5 h-5" />
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500">Zone 2 Pace Progression</span>
            {improvement > 0 && (
              <span className="ml-2 text-xs text-green-600 font-medium">
                {formatPace(improvement)} faster ({improvementPct}%)
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="z2PaceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="weekLabel"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              domain={[minPace, maxPace]}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
              tickFormatter={(v) => formatPace(v * 60)}
            />
            <Tooltip
              formatter={(value: number) => [formatPace(value * 60) + " /km", "Z2 Pace"]}
              labelFormatter={(label) => `Week of ${label}`}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="pace"
              stroke="#3b82f6"
              fill="url(#z2PaceGradient)"
              strokeWidth={2}
              dot={{ r: 4, fill: "#3b82f6", strokeWidth: 0 }}
              activeDot={{ r: 6, fill: "#2563eb", strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="text-xs text-gray-500 text-center mt-2">
        Higher on chart = slower pace at Zone 2 heart rate
      </div>
    </div>
  );
}

// Session type visual config (matches SessionCell.tsx)
const sessionTypeConfig: Record<
  SessionType,
  { icon: typeof BoltIcon; label: string; color: string; bgColor: string }
> = {
  easy: { icon: SunIcon, label: "Easy", color: "text-green-600", bgColor: "bg-green-50" },
  long: { icon: SparklesIcon, label: "Long", color: "text-blue-600", bgColor: "bg-blue-50" },
  tempo: { icon: FireIcon, label: "Tempo", color: "text-orange-600", bgColor: "bg-orange-50" },
  intervals: { icon: BoltIcon, label: "Intervals", color: "text-red-600", bgColor: "bg-red-50" },
  recovery: { icon: HeartIcon, label: "Recovery", color: "text-purple-600", bgColor: "bg-purple-50" },
  rest: { icon: MoonIcon, label: "Rest", color: "text-gray-400", bgColor: "bg-gray-50" },
};

function formatSessionDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) return "Today";
  if (date.getTime() === tomorrow.getTime()) return "Tomorrow";

  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatSessionDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

function formatSessionDistance(meters: number): string {
  return `${(meters / 1000).toFixed(1)} km`;
}

function getWeekProgress(createdAt: string, goalDate?: string): number | null {
  if (!goalDate) return null;
  const start = new Date(createdAt).getTime();
  const end = new Date(goalDate).getTime();
  const now = Date.now();
  if (end <= start) return null;
  return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
}

function formatGoalEvent(event: string): string {
  const labels: Record<string, string> = {
    marathon: "Marathon",
    half: "Half Marathon",
    "10k": "10K",
    "5k": "5K",
    custom: "Custom",
  };
  return labels[event] || event;
}

interface ActivePlansCardProps {
  plans: TrainingPlanWithMetrics[];
}

function ActivePlansCard({ plans }: ActivePlansCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
            <CalendarDaysIcon className="w-5 h-5" />
          </div>
          <span className="text-sm font-medium text-gray-500">Active Plans</span>
        </div>
        <Link
          to="/app/training-planner"
          className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
        >
          View <ArrowRightIcon className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="space-y-4">
        {plans.map((plan) => {
          const progress = getWeekProgress(plan.createdAt, plan.goalDate);
          return (
            <div key={plan.id}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-900">{plan.name}</span>
                {plan.goalEvent && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {formatGoalEvent(plan.goalEvent)}
                  </span>
                )}
              </div>
              {plan.goalDate && (
                <p className="text-xs text-gray-500 mb-2">
                  Goal: {new Date(plan.goalDate + "T00:00:00").toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              )}
              {progress !== null && (
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-brand-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
              {plan.metrics && plan.metrics.length > 0 && (
                <div className="mt-3">
                  <GoalMetrics metrics={plan.metrics} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface UpcomingSessionsCardProps {
  sessions: PlannedSession[];
}

function UpcomingSessionsCard({ sessions }: UpcomingSessionsCardProps) {
  const [selectedSession, setSelectedSession] = useState<PlannedSession | null>(null);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
            <BoltIcon className="w-5 h-5" />
          </div>
          <span className="text-sm font-medium text-gray-500">Upcoming Sessions</span>
        </div>
      </div>

      {sessions.length === 0 ? (
        <p className="text-sm text-gray-400">No upcoming sessions</p>
      ) : (
        <div className="space-y-3">
          {sessions.map((session) => {
            const config = sessionTypeConfig[session.sessionType];
            const Icon = config.icon;
            return (
              <button
                key={session.id}
                onClick={() => setSelectedSession(session)}
                className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer w-full text-left"
              >
                <div className={`p-1.5 rounded-lg ${config.bgColor}`}>
                  <Icon className={`w-4 h-4 ${config.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                    {(session.targetDuration || session.targetDistance) && (
                      <span className="text-xs text-gray-400">
                        {session.targetDuration && formatSessionDuration(session.targetDuration)}
                        {session.targetDuration && session.targetDistance && " · "}
                        {session.targetDistance && formatSessionDistance(session.targetDistance)}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatSessionDate(session.scheduledDate)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedSession && (
        <SessionDetailModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
        />
      )}
    </div>
  );
}

function formatActivityDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatActivityPace(secPerKm: number): string {
  const mins = Math.floor(secPerKm / 60);
  const secs = Math.round(secPerKm % 60);
  return `${mins}:${secs.toString().padStart(2, "0")} /km`;
}

interface RecentActivitiesCardProps {
  activities: ActivityListItem[];
}

function RecentActivitiesCard({ activities }: RecentActivitiesCardProps) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
            <BoltIcon className="w-5 h-5" />
          </div>
          <span className="text-sm font-medium text-gray-500">Recent Activities</span>
        </div>
        <Link
          to="/app/activities"
          className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
        >
          All <ArrowRightIcon className="w-3.5 h-3.5" />
        </Link>
      </div>

      {activities.length === 0 ? (
        <p className="text-sm text-gray-400">No activities yet</p>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <Link
              key={activity.id}
              to={`/app/activities/${activity.id}`}
              className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {activity.name}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{formatActivityDate(activity.startedAt)}</span>
                  <span>&middot;</span>
                  <span>{formatDistance(activity.distance)}</span>
                  {activity.averagePace && (
                    <>
                      <span>&middot;</span>
                      <span>{formatActivityPace(activity.averagePace)}</span>
                    </>
                  )}
                </div>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {formatDuration(activity.duration)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <ChartBarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">No activities yet</h3>
      <p className="text-gray-500">
        Start tracking your runs to see statistics here.
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-gray-100 rounded-lg w-9 h-9" />
            <div className="h-4 bg-gray-100 rounded w-24" />
          </div>
          <div className="h-7 bg-gray-100 rounded w-20" />
        </div>
      ))}
    </div>
  );
}

export function DashboardPage() {
  const { statistics, loading, error, timeRange, setTimeRange } = useStatistics("1y");
  const { plans, upcomingSessions, loading: plansLoading } = useDashboardPlans();
  const { activities: recentActivities, loading: activitiesLoading } = useRecentActivities(3);

  const showTrainingSection = !plansLoading && plans.length > 0;
  const showRecentActivities = !activitiesLoading && recentActivities.length > 0;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

        {/* Time Range Selector */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          {TIME_RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setTimeRange(option.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                timeRange === option.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Training Plans & Recent Activities Section */}
      {(showTrainingSection || showRecentActivities) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {showTrainingSection && <UpcomingSessionsCard sessions={upcomingSessions} />}
          {showRecentActivities && <RecentActivitiesCard activities={recentActivities} />}
          {showTrainingSection && <ActivePlansCard plans={plans} />}
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <LoadingState />
      ) : !statistics || statistics.totalActivities === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<BoltIcon className="w-5 h-5" />}
            label="Total Activities"
            value={statistics.totalActivities}
          />
          <StatCard
            icon={<FireIcon className="w-5 h-5" />}
            label="Current Streak"
            value={`${statistics.currentWeekStreak} ${statistics.currentWeekStreak === 1 ? "week" : "weeks"}`}
          />
          <StatCard
            icon={<TrophyIcon className="w-5 h-5" />}
            label="Longest Streak"
            value={`${statistics.longestWeekStreak} ${statistics.longestWeekStreak === 1 ? "week" : "weeks"}`}
          />
          <StatCard
            icon={<MapPinIcon className="w-5 h-5" />}
            label="Total Distance"
            value={formatDistance(statistics.totalDistance)}
          />
          <StatCard
            icon={<ClockIcon className="w-5 h-5" />}
            label="Total Time"
            value={formatDuration(statistics.totalDuration)}
          />
          <StatCard
            icon={<FireIcon className="w-5 h-5" />}
            label="Calories Burned"
            value={formatCalories(statistics.totalCalories)}
            subtitle="kcal"
          />
          <WorkoutBreakdownCard
            treadmill={statistics.treadmillCount}
            outdoor={statistics.outdoorCount}
          />
          {statistics.weeklyZ2Pace && statistics.weeklyZ2Pace.length > 1 && (
            <Z2PaceChart data={statistics.weeklyZ2Pace} />
          )}
        </div>
      )}
    </div>
  );
}
